import { Box, Dialog, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Button } from "src/domains/shared/components/Button";
import { Icon } from "src/domains/shared/components/Icon";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";

export interface DeleteConfirmDialogProps {
  open: boolean;
  /** 헤더 타이틀 (번역 완료 문자열) */
  title: string;
  /** 삭제 대상이 식별되는 주 메시지 — 이름·개수 interpolation 은 호출부 책임 */
  message: string;
  /** 복구 불가 안내 보조 문구 */
  subMessage: string;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteConfirmDialog({
  open,
  title,
  message,
  subMessage,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-label={title}
      PaperProps={{ sx: paper }}
    >
      <ModalHeader title={title} size="small" onClose={onCancel} />
      <Box sx={body}>
        <Box sx={iconCircle}>
          <Icon
            size={16}
            name="20/etc/trash"
            color={COLOR_CONSTANTS.error.main}
          />
        </Box>
        <Box sx={textCol}>
          <Typography sx={messageText}>{message}</Typography>
          <Typography sx={subMessageText}>{subMessage}</Typography>
        </Box>
      </Box>
      <Box sx={actions}>
        <Button
          emphasis="outlined"
          intent="inherit"
          size="small"
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        <Button
          emphasis="contained"
          intent="error"
          size="small"
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Dialog>
  );
}

const paper = {
  width: "300px",
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "8px",
} as const;

const body = {
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  padding: "14px 20px",
} as const;

const iconCircle = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  borderRadius: "50%",
  // 아이콘 색(error.main = #D32F2F = rgb(211, 47, 47))에서 배경 알파를 유도해
  // 에러색 변경 시 아이콘과 배경이 함께 바뀌도록 한다. 기존 리터럴과 결과가 byte 동일하다.
  backgroundColor: alpha(COLOR_CONSTANTS.error.main, 0.08),
} as const;

const textCol = {
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  minWidth: 0,
  paddingTop: "1px",
} as const;

const messageText = {
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: 1.45,
  color: "rgba(0, 0, 0, 0.85)",
  wordBreak: "break-all",
  whiteSpace: "pre-line",
} as const;

const subMessageText = {
  fontSize: "11px",
  lineHeight: 1.4,
  color: "rgba(0, 0, 0, 0.45)",
} as const;

const actions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px",
  padding: "0 20px 16px",
} as const;
